import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the best free invoice generator for India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Invilic is a free invoice generator built specifically for Indian businesses and freelancers. It supports GST invoices with GSTIN, HSN/SAC codes, CGST, SGST, and IGST calculations. Create up to 5 invoices per month for free, no sign-up required.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is GST mandatory on all invoices in India?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'GST is mandatory only for GST-registered businesses (those with annual turnover above the registration threshold: ₹20 lakh for services, ₹40 lakh for goods in most states). Businesses below the threshold can issue standard invoices without GST.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create invoices in Indian Rupees (INR)?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic supports INR (₹) as the default currency for Indian invoices. You can also create invoices in USD, GBP, EUR, or any other currency for international clients.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of invoices does Invilic support for Indian businesses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Invilic supports GST tax invoices (with GSTIN, HSN/SAC codes, CGST/SGST/IGST), standard freelancer invoices for unregistered businesses, and international invoices for Indian exporters billing foreign clients in foreign currency.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I add my UPI ID or bank details on the invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you add your bank account number, IFSC code, bank name, and branch to your invoice so Indian clients can make NEFT/RTGS transfers directly.',
      },
    },
  ],
};

export default function InvoiceGeneratorIndiaPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator India: Free GST & Standard Invoice Maker | Invilic"
        description="Free invoice generator for Indian businesses and freelancers. Create GST invoices with GSTIN, HSN/SAC codes, CGST/SGST/IGST. Standard invoices for non-GST businesses. Download PDF free."
        canonical="https://www.invilic.com/invoice-generator-india"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇮🇳 Made for India
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator India
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Indian freelancers and businesses. Create GST tax invoices with CGST/SGST/IGST, or standard invoices for non-GST businesses. Download professional PDF invoices, no account needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/gst"
              className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-emerald-600/20 transition-all duration-200"
            >
              Create GST Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200"
            >
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Which invoice to use */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Percent,
                title: 'GST Invoice',
                subtitle: 'For GST-registered businesses',
                features: ['GSTIN on invoice', 'HSN/SAC codes', 'CGST + SGST or IGST', 'Auto tax calculation'],
                to: '/create/gst',
                color: 'emerald',
                btnClass: 'bg-emerald-600 hover:bg-emerald-700',
              },
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For freelancers & small businesses',
                features: ['No GST required', 'Custom line items', 'Logo upload', 'Payment terms'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For Indian exporters',
                features: ['Foreign currency billing', 'Export invoice format', 'Multi-currency support', 'Client in any country'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Invoice Generator India: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF invoice for Indian businesses in under 2 minutes.</p>
          <Link
            to="/create/gst"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

